import { Button, cn } from "web-design-system";
import { InfoIcon, Shield, TrendingUpIcon } from "lucide-react";
import { RiskProfileResult, RiskProfileTier } from "./types";

const TIER_STYLES: Record<
  RiskProfileTier,
  { bg: string; fg: string; label: string }
> = {
  CONSERVATIVE: {
    bg: "bg-tag-green",
    fg: "text-tag-green-foreground",
    label: "Conservative",
  },
  MODERATE: {
    bg: "bg-tag-orange",
    fg: "text-tag-orange-foreground",
    label: "Moderate",
  },
  AGGRESSIVE: {
    bg: "bg-tag-red",
    fg: "text-tag-red-foreground",
    label: "Aggressive",
  },
};

export function ResultStep({
  result,
  onDone,
  onRetake,
}: {
  result: RiskProfileResult;
  onDone: () => void;
  onRetake?: () => void;
}) {
  const { bg, fg, label } = TIER_STYLES[result.tier];

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 min-h-0 px-15 overflow-y-auto flex flex-col justify-center items-center pt-4">
        <div
          className={cn(
            "relative flex size-30 items-center justify-center rounded-2xl",
            bg
          )}
        >
          <Shield strokeWidth={1} className={cn("size-20", fg)} />
          <TrendingUpIcon className={cn("absolute size-8", fg)} />
        </div>

        <span className="pt-8 text-xs uppercase tracking-wide text-muted-foreground">
          Risk Profile
        </span>
        <span className={cn("text-3xl font-semibold", fg)}>{label}</span>

        <div className="w-full pt-8">
          <div
            className="relative h-3 w-full rounded-sm"
            style={{
              background:
                "linear-gradient(90deg, var(--color-name-tag-emerald-foreground, #047857) 0%, var(--color-amber-500, #F59E0B) 50%, #D75126 80%, var(--color-name-tag-rose-foreground, #BE123C) 100%)",
            }}
          >
            <div
              className="absolute top-1/2 size-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-4 border-white bg-secondary shadow"
              style={{ left: `${Math.min(Math.max(result.score, 0), 100)}%` }}
            />
          </div>
          <div className="flex justify-between pt-2.5 text-xs text-muted-foreground">
            <span>Conservative</span>
            <span>Moderate</span>
            <span>Aggressive</span>
          </div>
        </div>
      </div>

      <div className="pt-9">
        <div className="flex w-full mb-5 gap-2 rounded-lg bg-card p-4">
          <InfoIcon
            strokeWidth={1.5}
            className="size-4 shrink-0 text-muted-foreground"
          />
          <p className="text-sm text-muted-foreground">{result.description}</p>
        </div>
        <div className="flex gap-4 items-center">
          {onRetake && (
            <Button variant="outline" className="flex-1" onClick={onRetake}>
              Retake
            </Button>
          )}
          <Button className="flex-1" onClick={onDone}>
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}
